import { createContext, useContext, useEffect, useState } from "react";
import { BaseHttpService } from "cms/core/services";

export const TranslationContext = createContext();

// RTL language abbreviations
const RTL_LANGUAGES = ['ar', 'he', 'ku', 'fa', 'ur', 'yi', 'ji'];

export const TranslationContextProvider = ({ children }) => {
  const [languages, setLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  // Load languages from backend
  const loadLanguages = async () => {
    try {
      const response = await BaseHttpService.loadLanguages();
      const languagesList = response?.results || response || [];
      setLanguages(languagesList);

      // Get saved language from localStorage or use default
      const savedLanguageId = localStorage.getItem('selectedLanguageId');
      const defaultLanguage = languagesList.find(lang => lang.is_default) || languagesList[0];
      const selectedLanguage = savedLanguageId
        ? languagesList.find(lang => lang.id === parseInt(savedLanguageId)) || defaultLanguage
        : defaultLanguage;

      if (selectedLanguage) {
        setCurrentLanguage(selectedLanguage);
        localStorage.setItem('selectedLanguageId', selectedLanguage.id.toString());
      }
    } catch (error) {
      console.error('Error loading languages:', error);
    }
  };

  // Load translations from backend
  const loadTranslations = async () => {
    try {
      const response = await BaseHttpService.loadTranslations();
      const translationsList = response?.results || response || [];
      
      // Transform translations into a key-value map for the current language
      const translationsMap = {};
      translationsList.forEach(translation => {
        if (translation.values && translation.values.length > 0) {
          // Find the translation value for the current language
          // Handle both cases: language as ID or as object
          const translationValue = translation.values.find(val => {
            const languageId = typeof val.language === 'object' ? val.language?.id : val.language;
            return languageId === currentLanguage?.id;
          });
          if (translationValue) {
            translationsMap[translation.key] = translationValue.value;
          } else {
            // Fallback to the key if translation not found
            translationsMap[translation.key] = translation.key;
          }
        } else {
          // Fallback to the key if no values
          translationsMap[translation.key] = translation.key;
        }
      });
      
      setTranslations(translationsMap);
    } catch (error) {
      console.error('Error loading translations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize: Load languages first, then translations
  useEffect(() => {
    const initialize = async () => {
      await loadLanguages();
    };
    initialize();
  }, []);

  // Load translations when current language changes
  useEffect(() => {
    if (currentLanguage) {
      setLoading(true);
      loadTranslations();
    }
  }, [currentLanguage]);

  // Update document direction and lang attribute when language changes
  useEffect(() => {
    if (currentLanguage) {
      const isRTL = RTL_LANGUAGES.includes(currentLanguage.abbreviation?.toLowerCase());
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = currentLanguage.abbreviation?.toLowerCase() || 'en';
      
      // Also update body and html class for CSS styling
      document.body.classList.remove('rtl', 'ltr', 'lan-rtl');
      document.documentElement.classList.remove('lan-rtl');
      if (isRTL) {
        document.body.classList.add('rtl', 'lan-rtl');
        document.documentElement.classList.add('lan-rtl');
      } else {
        document.body.classList.add('ltr');
      }
    }
  }, [currentLanguage]);

  // Translation function
  const _t = (key) => {
    if (!key) return '';
    if (loading) return key; // Return key while loading
    return translations[key] || key; // Return translation or fallback to key
  };

  // Change language
  const changeLanguage = (language) => {
    setCurrentLanguage(language);
    localStorage.setItem('selectedLanguageId', language.id.toString());
    setLoading(true);
  };

  const value = {
    _t,
    languages,
    currentLanguage,
    changeLanguage,
    loading,
    isRTL: currentLanguage ? RTL_LANGUAGES.includes(currentLanguage.abbreviation?.toLowerCase()) : false
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook to use translation context
export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationContextProvider');
  }
  return context;
};

